import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const BarReports = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [salesData, setSalesData] = useState({
    dailySales: [],
    topDrinks: [],
    categoryBreakdown: []
  });
  const [performanceData, setPerformanceData] = useState([]);

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchReportsData();
  }, [dateRange]);

  const fetchReportsData = async () => {
    setLoading(true);
    await Promise.all([fetchSalesData(), fetchPerformanceData()]);
    setLoading(false);
  };

  const fetchSalesData = async () => {
    try {
      const data = await apiGet(`/admin/bar/reports/sales?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`);
      if (data.success) {
        setSalesData(data.data);
      }
    } catch (error) {
      console.error('Error fetching sales data:', error);
    }
  };

  const fetchPerformanceData = async () => {
    try {
      const data = await apiGet(`/admin/bar/reports/performance?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`);
      if (data.success) {
        setPerformanceData(data.data);
      }
    } catch (error) {
      console.error('Error fetching performance data:', error);
    }
  };

  const getTotalRevenue = () => {
    return salesData.dailySales.reduce((sum, day) => sum + parseFloat(day.revenue || 0), 0);
  };

  const getTotalOrders = () => {
    return salesData.dailySales.reduce((sum, day) => sum + parseInt(day.order_count || 0), 0);
  };

  const getTotalItemsSold = () => {
    return salesData.dailySales.reduce((sum, day) => sum + parseInt(day.items_sold || 0), 0);
  };

  const getAverageOrderValue = () => {
    const total = getTotalRevenue();
    const orders = getTotalOrders();
    return orders > 0 ? (total / orders) : 0;
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    filterContainer: {
      display: 'flex',
      gap: '12px',
      marginBottom: '16px',
      flexWrap: 'wrap'
    },
    input: {
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      flex: isMobile ? '1 1 100%' : '0 0 auto'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading reports...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Bar Reports</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Sales & analytics' : 'Bar sales analytics, performance reports, and operational insights'}
        </p>
      </div>

      {/* Date Range Filter */}
      <div style={{ ...styles.contentCard, marginBottom: isMobile ? '16px' : '20px' }}>
        <h3 style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', marginBottom: '12px' }}>
          Date Range
        </h3>
        <div style={styles.filterContainer}>
          <input
            type="date"
            value={dateRange.start_date}
            onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
            style={styles.input}
          />
          <input
            type="date"
            value={dateRange.end_date}
            onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
            style={styles.input}
          />
          <button
            onClick={fetchReportsData}
            style={{
              ...styles.button,
              background: colors.primary,
              color: colors.white
            }}
          >
            Apply
          </button>
        </div>
      </div>

      {/* Summary Statistics */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.primary }}>
            ₹{getTotalRevenue().toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Total Revenue
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.success}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.success }}>
            {getTotalOrders()}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Total Orders
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.accent }}>
            {getTotalItemsSold()}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Items Sold
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.warning}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.warning }}>
            ₹{getAverageOrderValue().toFixed(0)}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Avg Order Value
          </div>
        </div>
      </div>

      {/* Daily Sales Chart */}
      {salesData.dailySales && salesData.dailySales.length > 0 && (
        <div style={styles.contentCard}>
          <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📊</span> Daily Sales
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Date</th>
                  <th style={styles.tableHeader}>Orders</th>
                  <th style={styles.tableHeader}>Items</th>
                  <th style={styles.tableHeader}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {salesData.dailySales.map((day, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>
                      <strong>{new Date(day.date).toLocaleDateString('en-IN')}</strong>
                    </td>
                    <td style={styles.tableCell}>{day.order_count}</td>
                    <td style={styles.tableCell}>{day.items_sold}</td>
                    <td style={styles.tableCell}>
                      <strong style={{ color: colors.success }}>
                        ₹{parseFloat(day.revenue).toLocaleString('en-IN')}
                      </strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Drinks */}
      {salesData.topDrinks && salesData.topDrinks.length > 0 && (
        <div style={styles.contentCard}>
          <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🔥</span> Top Selling Drinks
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Rank</th>
                  <th style={styles.tableHeader}>Drink Name</th>
                  <th style={styles.tableHeader}>Quantity Sold</th>
                  <th style={styles.tableHeader}>Orders</th>
                  <th style={styles.tableHeader}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {salesData.topDrinks.map((drink, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '700',
                        background: index === 0 ? `${colors.warning}30` : colors.lightGray,
                        color: index === 0 ? colors.warning : colors.textDark
                      }}>
                        #{index + 1}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <strong>{drink.item_name}</strong>
                    </td>
                    <td style={styles.tableCell}>{drink.quantity_sold}</td>
                    <td style={styles.tableCell}>{drink.order_count}</td>
                    <td style={styles.tableCell}>
                      <strong style={{ color: colors.primary }}>
                        ₹{parseFloat(drink.revenue).toLocaleString('en-IN')}
                      </strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {salesData.categoryBreakdown && salesData.categoryBreakdown.length > 0 && (
        <div style={styles.contentCard}>
          <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📂</span> Sales by Category
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '12px' }}>
            {salesData.categoryBreakdown.map((category, index) => (
              <div key={index} style={{
                padding: isMobile ? '14px' : '16px',
                background: colors.lightGray,
                borderRadius: '10px',
                border: `1px solid ${colors.mediumGray}`
              }}>
                <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', marginBottom: '6px', color: colors.primary }}>
                  {category.category_name}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: colors.textLight, marginBottom: '4px' }}>
                  <span>Items Sold:</span>
                  <strong style={{ color: colors.textDark }}>{category.items_sold}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ color: colors.textLight }}>Revenue:</span>
                  <strong style={{ color: colors.success }}>₹{parseFloat(category.revenue).toLocaleString('en-IN')}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bartender Performance */}
      {performanceData && performanceData.length > 0 && (
        <div style={styles.contentCard}>
          <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>👨‍🍳</span> Bartender Performance
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Staff Name</th>
                  <th style={styles.tableHeader}>Role</th>
                  <th style={styles.tableHeader}>Drinks Made</th>
                  {!isMobile && <th style={styles.tableHeader}>Avg Time (min)</th>}
                  {!isMobile && <th style={styles.tableHeader}>Min Time (min)</th>}
                  {!isMobile && <th style={styles.tableHeader}>Max Time (min)</th>}
                </tr>
              </thead>
              <tbody>
                {performanceData.map((staff, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>
                      <strong>{staff.staff_name}</strong>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{ textTransform: 'capitalize' }}>
                        {staff.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <strong style={{ color: colors.primary }}>{staff.drinks_made}</strong>
                    </td>
                    {!isMobile && (
                      <>
                        <td style={styles.tableCell}>{staff.avg_prep_time ? parseFloat(staff.avg_prep_time).toFixed(1) : 'N/A'}</td>
                        <td style={styles.tableCell}>{staff.min_prep_time || 'N/A'}</td>
                        <td style={styles.tableCell}>{staff.max_prep_time || 'N/A'}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!salesData.dailySales || salesData.dailySales.length === 0) &&
        (!salesData.topDrinks || salesData.topDrinks.length === 0) &&
        (!performanceData || performanceData.length === 0) && (
          <div style={styles.contentCard}>
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No data available</div>
              <div style={{ fontSize: '13px' }}>Sales data will appear here once orders are placed</div>
            </div>
          </div>
        )}
    </AdminLayout>
  );
};

export default BarReports;
